// src/contexts/AuthContext.tsx
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useGetUserByEmailQuery } from "../features/userApi";
import { keycloakService } from "../services/keycloakService";

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
}

interface AuthContextType {
  user: User | null;
  roles: string[];
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
  token: string | null;
  userId: string | null;
  isLoading?: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<string[]>([]);
  const [userId, setUserId] = useState<string | null>(null);

  const { data, isSuccess, isLoading, isError } = useGetUserByEmailQuery(
    user?.email,
    {
      refetchOnMountOrArgChange: true,
      skip: !user?.email,
    }
  );

  useEffect(() => {
    if (isSuccess && !isLoading && data?.userTypes?.length > 0 && !isError) {
      setRoles(data?.userTypes?.map((i) => i?.name));
      setUserId(data?.id);
    }
    if (isError) {
      // keycloakService.logout();
    }
  }, [isSuccess, isLoading, isError]);

  useEffect(() => {
    keycloakService.init().then((authenticated) => {
      if (authenticated) {
        const user = keycloakService.getUser();
        setUser(user);
        setIsAuthenticated(true);
        setLoading(false);
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
      }
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        userId,
        isAuthenticated,
        roles,
        token: keycloakService.getToken() || null,
        login: () => keycloakService.login(),
        logout: () => keycloakService.logout(),
        isLoading: loading || isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
